// client/src/services/youtubeService.js
// -------------------------------------------------------------
// Frontend YouTube helper service used by:
//   - YouTubeIntegration.jsx
//   - YouTubeAnalytics.jsx
//   - MediaPostEnhanced.jsx
//
// This file runs in the browser (React) and talks to your
// backend YouTube routes mounted at /api/youtube. Multi-tenant
// support is handled by always passing tenantId.
//
// Key exported functions:
//   getYouTubeAuthUrl(tenantId)
//   checkYouTubeConnection(tenantId, useCache?)
//   getYouTubeChannelInfo(tenantId, useCache?)
//   disconnectYouTube(tenantId)
//   uploadToYouTube({ ... })
//   getChannelVideos(tenantId, maxResults?, pageToken?, useCache?)
//   getVideoMetrics(tenantId, videoId, useCache?)
//   validateYouTubeFilePath(path)
//   extractYouTubeTitleDescription(text)
//   formatNumber(value)
//   formatDuration(isoDuration)
//   clearTenantCache(tenantId)
//
// NOTE: This file does NOT upload raw video files. Your videos
// should already be uploaded to the server (e.g. under /public)
// and you pass the server filePath into uploadToYouTube.
// -------------------------------------------------------------

// Base URL – in production you set REACT_APP_BASE_URL to https://on-screen.app
const RAW_BASE = (process.env.REACT_APP_BASE_URL || '').trim();

// Normalize so we don't end up with //api
const BASE_URL = RAW_BASE.replace(/\/+$/, '');
const API_BASE = BASE_URL ? `${BASE_URL}/api/youtube` : '/api/youtube';

// -------------------------
// Simple in-memory cache
// -------------------------

// ~2 minutes – enough to avoid spamming the API but still fresh
const CACHE_TTL_MS = 2 * 60 * 1000;

const _cache = new Map(); // key -> { value, ts }

function getCached(key) {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    _cache.delete(key);
    return null;
  }
  return entry.value;
}

function setCache(key, value) {
  _cache.set(key, { value, ts: Date.now() });
}

export function clearTenantCache(tenantId) {
  if (!tenantId) return;
  const prefix = `yt_${tenantId}_`;
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) {
      _cache.delete(key);
    }
  }
}

// -------------------------
// Internal helpers
// -------------------------

/**
 * Small helper to build URLs with query params.
 */
function buildUrl(base, params = {}) {
  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      url.searchParams.set(k, String(v));
    }
  });
  return url.toString();
}

/**
 * Helper to perform JSON fetch with credentials and consistent
 * error handling.
 */
async function fetchJson(url, options = {}) {
  const finalOpts = {
    method: 'GET',
    credentials: 'include', // send cookies for auth
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    ...options
  };

  const res = await fetch(url, finalOpts);
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    // If response is not JSON, keep data as null
  }

  if (!res.ok) {
    const message =
      (data && (data.message || data.error)) ||
      `Request failed with status ${res.status}`;
    return { ok: false, status: res.status, data, message };
  }

  return { ok: true, status: res.status, data };
}

/**
 * Normalize a video file path so the backend can resolve it.
 * Accepts either:
 *   - absolute server path (leave as-is)
 *   - '/public/tenants/...' style path
 *   - full URL like 'https://on-screen.app/public/...'
 */
function normalizeFilePath(filePath) {
  if (!filePath) return '';

  // If it looks like a URL, try to extract pathname
  if (/^https?:\/\//i.test(filePath)) {
    try {
      const url = new URL(filePath);
      return url.pathname || '';
    } catch (e) {
      // Fall through and return original
      return filePath;
    }
  }

  return filePath;
}

// -------------------------
// Public API functions
// -------------------------

/**
 * Get Google OAuth URL for a tenant.
 *
 * Backend route:
 *   GET /api/youtube/auth/url?tenantId=123
 */
export async function getYouTubeAuthUrl(tenantId) {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required to connect YouTube');
    }

    const url = buildUrl(`${API_BASE}/auth/url`, { tenantId });
    const { ok, data, message } = await fetchJson(url);

    if (!ok || !data?.success || !data?.url) {
      return {
        success: false,
        error: data?.message || message || 'Failed to create YouTube auth URL'
      };
    }

    return {
      success: true,
      url: data.url
    };
  } catch (error) {
    console.error('getYouTubeAuthUrl error:', error);
    return {
      success: false,
      error: error.message || 'Unexpected error creating YouTube auth URL'
    };
  }
}

/**
 * Disconnect YouTube for a tenant.
 *
 * Backend route:
 *   POST /api/youtube/disconnect
 *   Body: { tenantId }
 */
export async function disconnectYouTube(tenantId) {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required to disconnect YouTube');
    }

    const url = `${API_BASE}/disconnect`;
    const { ok, data, message } = await fetchJson(url, {
      method: 'POST',
      body: JSON.stringify({ tenantId })
    });

    if (!ok || !data?.success) {
      return {
        success: false,
        error: data?.message || message || 'Failed to disconnect YouTube'
      };
    }

    // Clear cached data for this tenant
    clearTenantCache(tenantId);

    return {
      success: true,
      message: data.message || 'YouTube disconnected successfully'
    };
  } catch (error) {
    console.error('disconnectYouTube error:', error);
    return {
      success: false,
      error: error.message || 'Unexpected error disconnecting YouTube'
    };
  }
}

/**
 * Check if YouTube is connected and (if connected) return
 * the raw channel object from the YouTube API.
 *
 * Backend route:
 *   GET /api/youtube/me?tenantId=123
 *
 * @returns {Promise<{connected:boolean, channel:any|null, error?:string}>}
 */
export async function checkYouTubeConnection(tenantId, useCache = true) {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    const cacheKey = `yt_${tenantId}_connection`;
    if (useCache) {
      const cached = getCached(cacheKey);
      if (cached) return cached;
    }

    const url = buildUrl(`${API_BASE}/me`, { tenantId });
    const { ok, data, message } = await fetchJson(url);

    if (!ok || !data?.success) {
      const result = {
        connected: false,
        channel: null,
        error: data?.message || message || 'Failed to check YouTube connection'
      };
      setCache(cacheKey, result);
      return result;
    }

    const channel = Array.isArray(data.data?.items)
      ? (data.data.items[0] || null)
      : null;

    const result = {
      connected: !!channel,
      channel,
      error: null
    };

    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('checkYouTubeConnection error:', error);
    return {
      connected: false,
      channel: null,
      error: error.message || 'Unexpected error checking YouTube connection'
    };
  }
}

/**
 * Get a flattened, UI-friendly channel object for display.
 * Uses checkYouTubeConnection under the hood so it also respects
 * caching and error handling.
 *
 * @returns {Promise<null|{
 *   id:string,
 *   title:string,
 *   description:string,
 *   customUrl?:string,
 *   thumbnails?:any,
 *   subscriberCount?:number,
 *   viewCount?:number,
 *   videoCount?:number,
 *   hiddenSubscriberCount?:boolean,
 *   raw:any
 * }>}
 */
export async function getYouTubeChannelInfo(tenantId, useCache = true) {
  const status = await checkYouTubeConnection(tenantId, useCache);

  if (!status.connected || !status.channel) {
    if (status.error) {
      // Propagate the error up so the caller can show a message
      throw new Error(status.error);
    }
    return null;
  }

  const channel = status.channel;
  const snippet = channel.snippet || {};
  const stats = channel.statistics || {};

  return {
    id: channel.id,
    title: snippet.title || '',
    description: snippet.description || '',
    customUrl: snippet.customUrl || '',
    thumbnails: snippet.thumbnails || {},
    subscriberCount: Number(stats.subscriberCount || 0),
    viewCount: Number(stats.viewCount || 0),
    videoCount: Number(stats.videoCount || 0),
    hiddenSubscriberCount: !!stats.hiddenSubscriberCount,
    raw: channel
  };
}

/**
 * Validate if a file path is suitable for YouTube upload.
 * Only basic client-side validation – the server will do
 * the final checks.
 *
 * Returns { valid:boolean, message?:string }
 */
export function validateYouTubeFilePath(filePath) {
  if (!filePath) {
    return { valid: false, message: 'Video file path is required' };
  }

  const normalized = normalizeFilePath(filePath);

  // Ensure it points somewhere under /public (where your
  // tenant media lives, e.g. /public/tenants/{id}/...)
  if (!normalized.includes('/public/')) {
    return {
      valid: false,
      message: 'Video must be stored under the /public directory on the server'
    };
  }

  // Very simple extension check. The backend ultimately
  // decides what is allowed.
  const allowed = ['.mp4', '.mov', '.m4v', '.webm'];
  const lower = normalized.toLowerCase();
  const hasExt = allowed.some(ext => lower.endsWith(ext));

  if (!hasExt) {
    return {
      valid: false,
      message: `Video must be one of: ${allowed.join(', ')}`
    };
  }

  return { valid: true };
}

/**
 * Upload a video that is already stored on the server to YouTube.
 *
 * Backend route:
 *   POST /api/youtube/upload
 *   Body: { tenantId, title, description, filePath, privacyStatus, tags, categoryId }
 *
 * @param {Object} options
 * @param {number} options.tenantId
 * @param {string} options.title
 * @param {string} options.description
 * @param {string} options.filePath  Server-side path (or URL) to video
 * @param {string} [options.privacyStatus='unlisted']
 * @param {string[]} [options.tags=[]]
 * @param {string} [options.categoryId='22']
 * @param {boolean} [options.isShort=false]  Mark upload as a YouTube Short
 * @param {function} [options.onProgress]  Optional progress callback
 *
 * @returns {Promise<{success:boolean, videoId?:string, url?:string, error?:string}>}
 */
export async function uploadToYouTube(options) {
  const {
    tenantId,
    title,
    description,
    filePath,
    privacyStatus = 'unlisted',
    tags = [],
    categoryId = '22',
    isShort = false,
    onProgress
  } = options || {};

  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    if (!filePath) {
      throw new Error('Video file path is required');
    }

    if (!title || !title.trim()) {
      throw new Error('Video title is required');
    }

    const normalizedPath = normalizeFilePath(filePath);
    const validation = validateYouTubeFilePath(normalizedPath);
    if (!validation.valid) {
      return { success: false, error: validation.message };
    }

    // Simulated progress – we don't stream the file here, the
    // server reads it from disk, so the HTTP request itself is small.
    if (onProgress) {
      onProgress({ stage: 'preparing', progress: 10 });
    }

    const url = `${API_BASE}/upload`;
    const payload = {
      tenantId,
      title: title.trim(),
      description: (description || '').trim(),
      filePath: normalizedPath,
      privacyStatus,
      tags,
      categoryId,
      isShort
    };

    if (onProgress) {
      onProgress({ stage: 'uploading', progress: 50 });
    }

    const { ok, data, message } = await fetchJson(url, {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (onProgress) {
      onProgress({ stage: 'finalizing', progress: 90 });
    }

    if (!ok || !data?.success) {
      return {
        success: false,
        error: data?.message || message || 'YouTube upload failed'
      };
    }

    if (onProgress) {
      onProgress({ stage: 'complete', progress: 100 });
    }

    return {
      success: true,
      videoId: data.videoId,
      url: data.url
    };
  } catch (error) {
    console.error('uploadToYouTube error:', error);
    if (onProgress) {
      onProgress({ stage: 'error', progress: 0, error: error.message });
    }
    return {
      success: false,
      error: error.message || 'Unexpected error uploading to YouTube'
    };
  }
}

/**
 * Get list of videos for the connected channel.
 *
 * Backend route:
 *   GET /api/youtube/videos?tenantId=123&maxResults=50&pageToken=xxx
 *
 * Returns a flattened array of video objects that match what
 * YouTubeAnalytics.jsx expects.
 *
 * @returns {Promise<{videos:Array, nextPageToken:string|null, totalResults:number, error?:string}>}
 */
export async function getChannelVideos(
  tenantId,
  maxResults = 50,
  pageToken = null,
  useCache = true
) {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    const safeMax = Math.min(Math.max(1, Number(maxResults) || 50), 50);
    const cacheKey = `yt_${tenantId}_videos_${safeMax}_${pageToken || 'first'}`;

    if (useCache && !pageToken) {
      const cached = getCached(cacheKey);
      if (cached) return cached;
    }

    const url = buildUrl(`${API_BASE}/videos`, {
      tenantId,
      maxResults: safeMax,
      pageToken: pageToken || undefined
    });

    const { ok, data, message } = await fetchJson(url);

    if (!ok || !data?.success) {
      const result = {
        videos: [],
        nextPageToken: null,
        totalResults: 0,
        error: data?.message || message || 'Failed to fetch videos'
      };
      setCache(cacheKey, result);
      return result;
    }

    // The backend already returns flattened video objects with
    // top-level keys (title, viewCount, etc.), so we map them
    // directly instead of trying to access nested YouTube API
    // properties (video.snippet.title, etc.) that don't exist.
    const flattenedVideos = (data.videos || []).map((video) => ({
      id: video.id,
      title: video.title || '',
      description: video.description || '',
      thumbnails: video.thumbnails || {},
      publishedAt: video.publishedAt || null,
      duration: video.duration || null,
      privacyStatus: video.privacyStatus || 'unknown',
      viewCount: Number(video.viewCount || 0),
      likeCount: Number(video.likeCount || 0),
      commentCount: Number(video.commentCount || 0),
      favoriteCount: Number(video.favoriteCount || 0),
      raw: video
    }));

    const result = {
      videos: flattenedVideos,
      nextPageToken: data.nextPageToken || null,
      totalResults: Number(data.totalResults || flattenedVideos.length),
      error: null
    };

    if (useCache && !pageToken) {
      setCache(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error('getChannelVideos error:', error);
    return {
      videos: [],
      nextPageToken: null,
      totalResults: 0,
      error: error.message || 'Unexpected error fetching videos'
    };
  }
}

/**
 * Get detailed metrics for a single video.
 *
 * Backend route:
 *   GET /api/youtube/video-metrics?tenantId=123&videoId=abc
 *
 * @returns {Promise<{
 *   videoId:string,
 *   title:string,
 *   description:string,
 *   viewCount:number,
 *   likeCount:number,
 *   commentCount:number,
 *   favoriteCount:number,
 *   publishedAt:string,
 *   duration:string,
 *   privacyStatus:string,
 *   embeddable?:boolean,
 *   license?:string,
 *   raw:any,
 *   error?:string
 * }>}
 */
export async function getVideoMetrics(tenantId, videoId, useCache = true) {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    if (!videoId) {
      throw new Error('videoId is required');
    }

    const cacheKey = `yt_${tenantId}_metrics_${videoId}`;
    if (useCache) {
      const cached = getCached(cacheKey);
      if (cached) return cached;
    }

    const url = buildUrl(`${API_BASE}/video-metrics`, {
      tenantId,
      videoId
    });

    const { ok, data, message } = await fetchJson(url);

    if (!ok || !data?.success) {
      const result = {
        videoId,
        error: data?.message || message || 'Failed to fetch video metrics'
      };
      setCache(cacheKey, result);
      return result;
    }

    const video = data.video || {};
    const snippet = video.snippet || {};
    const stats = video.statistics || {};
    const details = video.contentDetails || {};
    const status = video.status || {};

    const metrics = {
      videoId: video.id || videoId,
      title: snippet.title || '',
      description: snippet.description || '',
      viewCount: Number(stats.viewCount || 0),
      likeCount: Number(stats.likeCount || 0),
      commentCount: Number(stats.commentCount || 0),
      favoriteCount: Number(stats.favoriteCount || 0),
      publishedAt: snippet.publishedAt || null,
      duration: details.duration || null,
      privacyStatus: status.privacyStatus || 'unknown',
      embeddable: typeof status.embeddable === 'boolean'
        ? status.embeddable
        : undefined,
      license: status.license || undefined,
      raw: video,
      error: null
    };

    setCache(cacheKey, metrics);
    return metrics;
  } catch (error) {
    console.error('getVideoMetrics error:', error);
    return {
      videoId,
      error: error.message || 'Unexpected error fetching video metrics'
    };
  }
}

// -------------------------
// Formatting helpers
// -------------------------

/**
 * Format numeric values for UI (e.g. 12345 -> "12.3K")
 */
export function formatNumber(value) {
  if (value === null || value === undefined) return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  if (n >= 1_000_000_000) {
    return (n / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (n >= 1_000_000) {
    return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (n >= 1_000) {
    return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  }

  return n.toLocaleString();
}

/**
 * Convert ISO 8601 YouTube duration into a human-readable form.
 * Example inputs: "PT15M33S", "PT2H3M", "PT45S"
 * Example outputs: "15:33", "2:03:00", "0:45"
 */
export function formatDuration(isoDuration) {
  if (!isoDuration || typeof isoDuration !== 'string') {
    return '';
  }

  const match = isoDuration.match(
    /P(?:\d+Y)?(?:\d+M)?(?:\d+W)?(?:\d+D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/i
  );

  if (!match) return isoDuration;

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);

  const pad = (num) => String(num).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${minutes}:${pad(seconds)}`;
}

/**
 * Extract a reasonable YouTube title + description from the
 * free-text content used in your social post composer.
 *
 * Strategy:
 *   - Use the first line (or first 100 chars) as the title
 *   - Use the rest of the text as the description
 */
export function extractYouTubeTitleDescription(text) {
  if (!text) {
    return {
      title: 'Untitled video',
      description: ''
    };
  }

  const trimmed = String(text).trim();
  const MAX_TITLE_LEN = 100;

  // Split on first newline if present
  const newlineIndex = trimmed.indexOf('\n');

  let title;
  let description;

  if (newlineIndex !== -1) {
    title = trimmed.slice(0, newlineIndex).trim();
    description = trimmed.slice(newlineIndex + 1).trim();
  } else if (trimmed.length > MAX_TITLE_LEN) {
    title = trimmed.slice(0, MAX_TITLE_LEN).trim();
    description = trimmed;
  } else {
    title = trimmed;
    description = trimmed;
  }

  if (!title) {
    title = 'Untitled video';
  }

  return { title, description };
}
